import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import api, { setAccessToken } from './api';

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
});

const STORAGE_KEY = 'chiv_admin_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On boot, try to refresh the session (refresh cookie may still be valid)
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (!cached) {
          setLoading(false);
          return;
        }
        const res = await api.post('/auth/refresh');
        if (cancelled) return;
        const token = res?.data?.accessToken;
        if (token) {
          setAccessToken(token);
          setUser(JSON.parse(cached));
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await api.post('/auth/login', { username, password });
    const { user: u, accessToken } = res.data;
    setAccessToken(accessToken);
    setUser(u);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    setAccessToken(null);
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null; // brief flash; could render a spinner here
  if (!user || user.role !== 'admin') {
    return <Navigate to="/admin" state={{ from: location }} replace />;
  }
  return children;
}
